"use client";

import {
  MemoDetail,
  MemoForm,
  MemoList,
  PanelHeader,
} from "./memo-panel-components";
import { useMemoPanelController } from "./memo-panel-controller";

/**
 * User memo 用の右 side panel を描画します。
 * data / mutation / intent 制御は controller hook に閉じ、ここでは現在 state に合う surface だけを組み立てます。
 */
export function MemoSidePanel() {
  const controller = useMemoPanelController();
  const { context, userMemoIntent } = controller;

  if (!userMemoIntent || !context) {
    return (
      <div className="flex h-full w-full flex-col border-l border-border/70 bg-background">
        <PanelHeader
          title={controller.panelTitle}
          onClose={controller.closeMemoPanel}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col border-l border-border/70 bg-background">
      <PanelHeader
        title={controller.panelTitle}
        onClose={controller.closeMemoPanel}
        onBack={
          userMemoIntent.state === "list"
            ? undefined
            : () => controller.openMemoList()
        }
      />
      {userMemoIntent.state === "create" ||
      (userMemoIntent.state === "edit" && controller.selectedMemoCanEdit) ? (
        controller.createOrEditForm ? (
          <MemoForm
            form={controller.createOrEditForm}
            formError={controller.formError}
            createTargetOptions={controller.createTargetOptions}
            selectedCreateTargetKey={controller.selectedCreateTargetKey}
            canSelectProjectScope={Boolean(context.projectId)}
            featureKind={context.featureKind}
            targetKind={context.targetKind}
            onChange={controller.handleFormChange}
            onCreateTargetChange={controller.handleCreateTargetChange}
            onCancel={() => controller.openMemoList()}
            onSave={controller.handleSave}
            isSubmitting={controller.isSavingMemo}
            showDateContext={
              userMemoIntent.state === "create" &&
              userMemoIntent.source === "chart"
            }
          />
        ) : null
      ) : userMemoIntent.state === "detail" ||
        userMemoIntent.state === "edit" ? (
        controller.selectedMemoCanEdit && controller.selectedMemo ? (
          controller.selectedMemoForm ? (
            <MemoForm
              form={controller.selectedMemoForm}
              formError={controller.formError}
              memo={controller.selectedMemo}
              createTargetOptions={controller.createTargetOptions}
              selectedCreateTargetKey={controller.selectedCreateTargetKey}
              canSelectProjectScope={Boolean(context.projectId)}
              featureKind={context.featureKind}
              targetKind={context.targetKind}
              onChange={controller.handleFormChange}
              onCreateTargetChange={controller.handleCreateTargetChange}
              onCancel={() => controller.openMemoList()}
              onSave={controller.handleSave}
              isSubmitting={controller.isSavingMemo}
              onArchive={() =>
                controller.handleDelete(controller.selectedMemo!)
              }
              showDateContext={false}
            />
          ) : null
        ) : (
          <MemoDetail
            memo={controller.selectedMemo}
            fetching={controller.fetching}
            onDelete={controller.handleDelete}
            onRestore={controller.handleRestore}
          />
        )
      ) : (
        <MemoList
          memos={controller.memos}
          fetching={controller.fetching}
          error={controller.errorMessage}
          filter={controller.filter}
          chartHoveredMemoIds={controller.chartHoveredMemoIds}
          listHoveredMemoId={controller.listHoveredMemoId}
          memoDisplaySettings={controller.memoDisplaySettings}
          availableAuthors={controller.availableAuthors}
          systemMemoLabels={controller.systemMemoLabels}
          featureKind={context.featureKind}
          targetKind={context.targetKind}
          onFilterChange={controller.handleFilterChange}
          onFilterQueryChange={controller.handleFilterQueryChange}
          onMemoDisplaySettingsChange={controller.writeMemoDisplaySettings}
          onAdd={controller.handleAdd}
          canAdd={controller.canUseUserMemoEditActions}
          onMemoHover={controller.highlightChartMemoFromList}
          onMemoHoverEnd={controller.clearChartMemoFromList}
          onOpen={controller.openMemoDetail}
        />
      )}
    </div>
  );
}
